import React, { useState, useRef, useEffect } from "react";
import { useMutation } from "react-query";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api from "../../../services/api";

const ChatInterface = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const messagesEndRef = useRef(null);
  const toast = useRef(null);
  const textareaRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const askAIMutation = useMutation(
    async (question) => {
      const response = await api.post("/ai/ask", { question });
      return response.data;
    },
    {
      onSuccess: (data) => {
        // Remove the loading message first
        setMessages((prev) => prev.filter(msg => msg.type !== "loading"));
        
        if (data.success) {
          setMessages((prev) => [
            ...prev,
            {
              type: "ai",
              text: data.answer,
              sql: data.generated_sql,
              resultCount: data.result_count,
              timestamp: new Date()
            }
          ]);
        } else {
          toast.current?.show({
            severity: "error",
            summary: "Erro",
            detail: data.error || "Erro ao processar pergunta",
            life: 5000
          });
        }
      },
      onError: (error) => {
        // Remove the loading message
        setMessages((prev) => prev.filter(msg => msg.type !== "loading"));
        
        const errorMessage =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          "Erro ao conectar com o servidor";
        
        toast.current?.show({
          severity: "error",
          summary: "Erro",
          detail: errorMessage,
          life: 5000
        });
      }
    }
  );

  const handleSend = () => {
    if (!input.trim()) return;

    const userMessage = {
      type: "user",
      text: input.trim(),
      timestamp: new Date()
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");

    // Add loading message
    setMessages((prev) => [
      ...prev,
      {
        type: "loading",
        timestamp: new Date()
      }
    ]);

    askAIMutation.mutate(input.trim());

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = "50px";
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleTextareaChange = (e) => {
    setInput(e.target.value);
    
    // Auto-resize textarea
    if (textareaRef.current) {
      textareaRef.current.style.height = "50px";
      textareaRef.current.style.height = `${Math.min(
        textareaRef.current.scrollHeight,
        150
      )}px`;
    }
  };

  const clearChat = () => {
    setMessages([]);
    toast.current?.show({
      severity: "info",
      summary: "Chat limpo",
      detail: "Todas as mensagens foram removidas",
      life: 3000
    });
  };

  return (
    <div className="chat-interface">
      <Toast ref={toast} position="top-right" />
      
      <div className="chat-messages">
        {messages.length === 0 ? (
          <div className="empty-chat">
            <i className="pi pi-comments" />
            <h3>Bem-vindo ao Assistente de IA</h3>
            <p>Faça uma pergunta sobre seus dados para começar</p>
            <div style={{ marginTop: "20px", textAlign: "center", maxWidth: "600px" }}>
              <p style={{ fontSize: "13px", color: "#6c757d", marginBottom: "10px" }}>
                <strong>Exemplos de perguntas:</strong>
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <div
                  style={{
                    padding: "10px",
                    background: "#f8f9fa",
                    borderRadius: "6px",
                    fontSize: "12px",
                    cursor: "pointer",
                    transition: "background 0.2s"
                  }}
                  onClick={() => setInput("Quantos chamados abertos temos hoje?")}
                  onMouseEnter={(e) => e.target.style.background = "#e9ecef"}
                  onMouseLeave={(e) => e.target.style.background = "#f8f9fa"}
                >
                  "Quantos chamados abertos temos hoje?"
                </div>
                <div
                  style={{
                    padding: "10px",
                    background: "#f8f9fa",
                    borderRadius: "6px",
                    fontSize: "12px",
                    cursor: "pointer",
                    transition: "background 0.2s"
                  }}
                  onClick={() => setInput("Quais são os últimos 5 relatórios criados?")}
                  onMouseEnter={(e) => e.target.style.background = "#e9ecef"}
                  onMouseLeave={(e) => e.target.style.background = "#f8f9fa"}
                >
                  "Quais são os últimos 5 relatórios criados?"
                </div>
                <div
                  style={{
                    padding: "10px",
                    background: "#f8f9fa",
                    borderRadius: "6px",
                    fontSize: "12px",
                    cursor: "pointer",
                    transition: "background 0.2s"
                  }}
                  onClick={() => setInput("Quantos equipamentos cada empresa possui?")}
                  onMouseEnter={(e) => e.target.style.background = "#e9ecef"}
                  onMouseLeave={(e) => e.target.style.background = "#f8f9fa"}
                >
                  "Quantos equipamentos cada empresa possui?"
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            {messages.map((message, index) => (
              <div key={index} className={`chat-message ${message.type}`}>
                <div className="message-avatar">
                  {message.type === "user" ? (
                    <i className="pi pi-user" />
                  ) : message.type === "loading" ? (
                    <i className="pi pi-spin pi-spinner" />
                  ) : (
                    <i className="pi pi-sparkles" />
                  )}
                </div>
                <div className="message-content">
                  {message.type === "loading" ? (
                    <div className="loading-message">
                      <span>Pensando</span>
                      <div className="loading-dots">
                        <div className="loading-dot"></div>
                        <div className="loading-dot"></div>
                        <div className="loading-dot"></div>
                      </div>
                    </div>
                  ) : (
                    <>
                      {message.type === "user" ? (
                        <p className="message-text">{message.text}</p>
                      ) : (
                        <div className="message-text markdown-content">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {message.text}
                          </ReactMarkdown>
                        </div>
                      )}
                      {message.sql && (
                        <div className="message-metadata">
                          <div className="metadata-section">
                            <div className="metadata-label">SQL Gerado</div>
                            <div className="sql-query">{message.sql}</div>
                          </div>
                          {message.resultCount !== undefined && (
                            <div className="result-count">
                              <i className="pi pi-check-circle" style={{ marginRight: "6px" }} />
                              {message.resultCount} resultado(s) encontrado(s)
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      <div className="chat-input-container">
        {messages.length > 0 && (
          <div style={{ marginBottom: "12px", display: "flex", justifyContent: "flex-end" }}>
            <Button
              label="Limpar Chat"
              icon="pi pi-trash"
              className="p-button-text p-button-sm"
              onClick={clearChat}
              style={{ color: "#dc3545" }}
            />
          </div>
        )}
        <div className="chat-input-wrapper">
          <div className="chat-input-field">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={handleTextareaChange}
              onKeyPress={handleKeyPress}
              placeholder="Digite sua pergunta... (Shift+Enter para nova linha)"
              disabled={askAIMutation.isLoading}
            />
          </div>
          <button
            className="chat-send-button"
            onClick={handleSend}
            disabled={!input.trim() || askAIMutation.isLoading}
          >
            {askAIMutation.isLoading ? (
              <i className="pi pi-spin pi-spinner" />
            ) : (
              <i className="pi pi-send" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;

